<template>
	<div class="clip-timeline flex w-full min-w-0 flex-col gap-2">
		<div class="flex items-center justify-between gap-2 text-tiny text-surface-8">
			<div class="flex min-w-0 items-center gap-2">
				<span class="font-medium text-surface-text-1">Timeline</span>
				<span class="hidden truncate tabular-nums sm:inline">{{ viewSpanLabel }}</span>
			</div>

			<!-- Toolbar: ghost buttons with 44px hit areas (::after), grouped like the dashboard toolbars -->
			<div class="flex items-center gap-1" role="toolbar" aria-label="Timeline tools">
				<div class="flex items-center gap-0.5 rounded-md bg-surface-1 p-0.5 ring-1 ring-surface-3">
					<FormButton type="link" size="sm" is-icon class="clip-timeline__tool" aria-label="Zoom out" title="Zoom out (-)" @click="zoomBy(1.6)">
						<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
					</FormButton>
					<FormButton type="link" size="sm" is-icon class="clip-timeline__tool" aria-label="Zoom in" title="Zoom in (+)" @click="zoomBy(1 / 1.6)">
						<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M8 3v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
					</FormButton>
					<span class="mx-0.5 h-4 w-px bg-surface-4" aria-hidden="true" />
					<FormButton type="link" size="sm" class="clip-timeline__tool !px-2" :disabled="!selection" :title="selection ? 'Zoom to the current clip' : 'Select a clip first'" @click="fitSelection">
						Fit selection
					</FormButton>
					<FormButton type="link" size="sm" class="clip-timeline__tool !px-2" :disabled="!ranges.length" title="Show the whole recording window" @click="fitDvr">
						Fit DVR
					</FormButton>
				</div>
				<div class="relative">
					<FormButton
						type="link"
						size="sm"
						class="clip-timeline__tool !px-2 ring-1 ring-surface-3 bg-surface-1"
						title="Keyboard shortcuts (?)"
						aria-label="Keyboard shortcuts"
						aria-haspopup="dialog"
						:aria-expanded="showShortcuts ? 'true' : 'false'"
						@click="toggleShortcuts"
					>
						<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3" fill="none" /><path d="M4 6.5h1M6.5 6.5h1M9 6.5h1M11.5 6.5h1M4 9.5h1M6.5 9.5h3M11.5 9.5h1" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" /></svg>
						<span class="ml-1.5 hidden lg:inline">Shortcuts</span>
						<kbd class="clip-timeline__kbd ml-1.5 hidden lg:inline-block">?</kbd>
					</FormButton>
					<!-- Teleport to body so layout overflow / ancestor transforms cannot clip the panel. -->
					<Teleport to="body">
						<div
							v-if="showShortcuts"
							class="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] md:items-center"
							@click.self="showShortcuts = false"
						>
							<div
								class="max-h-full w-full max-w-[420px] overflow-y-auto overscroll-contain rounded-lg bg-surface-2 p-4 text-left shadow-xl ring-1 ring-surface-4"
								role="dialog"
								aria-modal="true"
								aria-label="Keyboard shortcuts"
								@click.stop
								@keydown.esc.stop="showShortcuts = false"
							>
								<div class="sticky top-0 z-10 -mx-4 -mt-4 mb-3 flex items-center justify-between bg-surface-2 px-4 pt-4 pb-1">
									<span class="text-sm font-medium text-surface-text-2">Keyboard shortcuts</span>
									<button type="button" class="rounded px-1.5 text-xs text-surface-8 hover:text-surface-text-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-main" @click="showShortcuts = false">Close</button>
								</div>
								<p class="mb-3 text-xs text-surface-8">Shortcuts pause while you are typing in a field.</p>
								<div class="grid gap-3 sm:grid-cols-2">
									<section v-for="group in SHORTCUT_GROUPS" :key="group.title">
										<h4 class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-surface-8">{{ group.title }}</h4>
										<dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
											<template v-for="sc in group.items" :key="sc.keys">
												<dt class="whitespace-nowrap"><kbd class="clip-timeline__kbd">{{ sc.keys }}</kbd></dt>
												<dd class="m-0 text-surface-text-1">{{ sc.label }}</dd>
											</template>
										</dl>
									</section>
								</div>
							</div>
						</div>
					</Teleport>
				</div>
			</div>
		</div>

		<div
			ref="stageEl"
			class="clip-timeline__stage relative w-full select-none rounded-lg bg-surface-1 ring-1 ring-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
			:class="stageCursorClass"
			:style="{ height: `${STAGE_HEIGHT}px` }"
			tabindex="0"
			role="group"
			aria-label="Clip timeline. Click to seek, drag to pan, scroll to zoom, hold Shift and drag to select."
			@pointerdown="onPointerDown"
			@pointermove="onPointerMove"
			@pointerup="onPointerUp"
			@pointercancel="onPointerUp"
			@pointerleave="onPointerLeave"
			@keydown="onKeyDown"
			@keyup="onKeyUp"
			@blur="stopKeyHold"
		>
			<canvas ref="canvasEl" class="absolute inset-0 block h-full w-full rounded-lg" />

			<!-- Accessible selection handles (also pointer targets) -->
			<template v-if="selection && selectionVisible">
				<div
					class="clip-timeline__handle"
					:style="{ left: `${selectionPx.start - 6}px`, top: `${TRACK_TOP}px`, height: `${TRACK_HEIGHT}px` }"
					role="slider"
					tabindex="0"
					aria-label="Clip start"
					aria-orientation="horizontal"
					:aria-valuemin="String(Math.round(handleBounds.start.min))"
					:aria-valuemax="String(Math.round(handleBounds.start.max))"
					:aria-valuenow="String(Math.round(selection.start))"
					:aria-valuetext="`Clip start ${formatLocalTime(selection.start)}`"
					@keydown.stop="onHandleKey($event, 'start')"
				/>
				<div
					class="clip-timeline__handle"
					:style="{ left: `${selectionPx.end - 6}px`, top: `${TRACK_TOP}px`, height: `${TRACK_HEIGHT}px` }"
					role="slider"
					tabindex="0"
					aria-label="Clip end"
					aria-orientation="horizontal"
					:aria-valuemin="String(Math.round(handleBounds.end.min))"
					:aria-valuemax="String(Math.round(handleBounds.end.max))"
					:aria-valuenow="String(Math.round(selection.end))"
					:aria-valuetext="`Clip end ${formatLocalTime(selection.end)}`"
					@keydown.stop="onHandleKey($event, 'end')"
				/>
				<div
					class="pointer-events-none absolute -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white shadow"
					:class="selectionBad ? 'bg-error' : 'bg-main'"
					:style="{ left: `${durationPillX}px`, top: '3px' }"
				>
					{{ formatClock(selection.end - selection.start) }}<template v-if="!selectionContiguous"> · crosses a gap</template><template v-else-if="selectionTooLong"> · over limit</template>
				</div>
			</template>

			<!-- Live chip -->
			<div
				v-if="liveEdgePx !== null"
				class="pointer-events-none absolute bottom-[3px] -translate-x-1/2 rounded-full bg-error px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-white"
				:style="{ left: `${clamp(liveEdgePx, 16, width - 16)}px` }"
			>
				Live
			</div>

			<!-- Hover tooltip + thumbnail -->
			<div
				v-if="hover && !drag"
				class="pointer-events-none absolute z-10 flex flex-col items-center gap-1"
				:style="{ left: `${clamp(hover.x, 70, width - 70)}px`, bottom: `${STAGE_HEIGHT - TRACK_TOP + 6}px`, transform: 'translateX(-50%)' }"
			>
				<img
					v-if="thumbSrc && hover.inRange"
					:src="thumbSrc"
					alt=""
					class="h-[72px] w-[128px] rounded-md bg-black object-cover ring-1 ring-surface-4"
					:class="{ 'opacity-0': thumbBroken }"
					@error="thumbBroken = true"
					@load="thumbBroken = false"
				>
				<span class="rounded bg-black/85 px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-white">
					{{ formatLocalTime(hover.t) }}
				</span>
			</div>

			<div
				v-if="!ranges.length"
				class="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-tiny text-surface-8"
			>
				<div v-if="loading" class="clip-timeline__skeleton absolute inset-x-3 flex flex-col gap-2" :style="{ top: `${TRACK_TOP + 8}px` }" aria-hidden="true">
					<span class="h-2 w-1/3 rounded bg-surface-3" />
					<span class="h-8 w-full rounded bg-surface-3" />
				</div>
				<slot v-else name="empty">Recordings will appear here as soon as the stream is live.</slot>
			</div>
		</div>

		<!-- Visually hidden shortcut list for screen readers -->
		<ul class="sr-only" aria-label="Keyboard shortcuts">
			<li v-for="sc in SHORTCUTS" :key="sc.keys">{{ sc.keys }}: {{ sc.label }}</li>
		</ul>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import { createHoldRepeater } from './holdRepeat';
import {
	rangeAt,
	inRange,
	selectionIsContiguous,
	gapsBetween,
	pickTickStep,
	formatLocalTime,
	formatLocalDate,
	formatClock,
	thumbnailUrl,
	clamp,
} from './dvrUtils';

const props = defineProps({
	ranges: { type: Array, default: () => [] },
	// { from, duration } seconds
	view: { type: Object, required: true },
	playhead: { type: Number, default: null },
	liveEdge: { type: Number, default: null },
	// { start, end } | null
	selection: { type: Object, default: null },
	maxDuration: { type: Number, default: 900 },
	dvrExtent: { type: Object, default: null },
	baseUrl: { type: String, default: '' },
	// Keep the playhead in view while it advances (live / replay).
	follow: { type: Boolean, default: true },
	// { start, end } the origin will deliver for the committed selection
	// (segment-aligned, so usually a little wider than the selection).
	exportWindow: { type: Object, default: null },
	// DVR metadata still loading: show a skeleton instead of the empty copy.
	loading: { type: Boolean, default: false },
});

const emit = defineEmits([
	'update:view',
	'update:selection',
	'seek',
	'toggle-play',
	'download',
	'go-live',
	'user-interaction',
	// true when a handle / body / new-selection drag or a held key starts,
	// false when it ends. The parent commits the selection on false.
	'interaction',
]);

const STAGE_HEIGHT = 112;
const RULER_HEIGHT = 22;
const TRACK_TOP = 30;
const TRACK_BOTTOM = STAGE_HEIGHT - 8;
const TRACK_HEIGHT = TRACK_BOTTOM - TRACK_TOP;
const MIN_VIEW_SEC = 60;
// Half-width of the handle hit zone: 44px targets for touch, 28px for mouse.
const HANDLE_HIT_PX = { mouse: 14, touch: 22, pen: 14 };

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const MOD = IS_MAC ? 'Cmd' : 'Ctrl';

const SHORTCUT_GROUPS = [
	{
		title: 'Playback',
		items: [
			{ keys: 'Space', label: 'Play or pause' },
			{ keys: '← / →', label: 'Step 5s (Shift: 30s)' },
			{ keys: 'J / L', label: 'Step 10s back / forward' },
			{ keys: 'End', label: 'Jump to live' },
		],
	},
	{
		title: 'Marking',
		items: [
			{ keys: 'I', label: 'Set clip start at the playhead' },
			{ keys: 'O', label: 'Set clip end at the playhead' },
			{ keys: 'Shift + drag', label: 'Select a new clip on the timeline' },
		],
	},
	{
		title: 'Trimming',
		items: [
			{ keys: '[ / ]', label: 'Nudge start / end by 1s, hold to repeat (Shift reverses)' },
			{ keys: `${MOD} + Enter`, label: 'Download the clip' },
			{ keys: 'Esc', label: 'Clear the selection' },
		],
	},
	{
		title: 'Navigation',
		items: [
			{ keys: '+ / -', label: 'Zoom in / out' },
			{ keys: 'Home', label: 'Jump to the earliest recording' },
			{ keys: 'Drag', label: 'Pan the timeline' },
			{ keys: '?', label: 'Show shortcuts' },
		],
	},
];
const SHORTCUTS = SHORTCUT_GROUPS.flatMap((g) => g.items);

const stageEl = ref(null);
const canvasEl = ref(null);
const width = ref(0);
const hover = ref(null); // { x, t, inRange }
const drag = ref(null); // { kind, ... }
const showShortcuts = ref(false);
function toggleShortcuts() {
	showShortcuts.value = !showShortcuts.value;
}
const thumbSrc = ref('');
const thumbBroken = ref(false);

let ctx = null;
let dpr = 1;
let rafId = 0;
let resizeObserver = null;
let thumbTimer = null;
let lastThumbSec = null;
const pointers = new Map(); // pointerId -> { x, y }
let pinchStart = null;

// ---- time <-> px ----------------------------------------------------------

function tToX(t) {
	return ((t - props.view.from) / props.view.duration) * width.value;
}

function xToT(x) {
	return props.view.from + (x / width.value) * props.view.duration;
}

const viewEnd = computed(() => props.view.from + props.view.duration);

const selectionVisible = computed(() => {
	if (!props.selection) return false;
	return props.selection.end >= props.view.from && props.selection.start <= viewEnd.value;
});

const selectionPx = computed(() => {
	if (!props.selection) return { start: 0, end: 0 };
	return { start: tToX(props.selection.start), end: tToX(props.selection.end) };
});

const durationPillX = computed(() => {
	const a = clamp(selectionPx.value.start, 0, width.value);
	const b = clamp(selectionPx.value.end, 0, width.value);
	return clamp((a + b) / 2, 28, width.value - 28);
});

const selectionTooLong = computed(() => !!props.selection && props.selection.end - props.selection.start > props.maxDuration + 0.001);

const liveEdgePx = computed(() => {
	if (typeof props.liveEdge !== 'number') return null;
	const x = tToX(props.liveEdge);
	if (x < -20 || x > width.value + 20) return null;
	return x;
});

const viewSpanLabel = computed(() => {
	const from = props.view.from;
	const to = viewEnd.value;
	const sameDay = formatLocalDate(from) === formatLocalDate(to);
	const a = `${formatLocalDate(from)} ${formatLocalTime(from, false)}`;
	const b = sameDay ? formatLocalTime(to, false) : `${formatLocalDate(to)} ${formatLocalTime(to, false)}`;
	return `${a} to ${b}`;
});

const selectionContiguous = computed(() => selectionIsContiguous(props.ranges, props.selection));
const selectionBad = computed(() => !!props.selection && (selectionTooLong.value || !selectionContiguous.value));

// Handles stop at the edges of the recording that contains the selection.
// A selection that already crosses a gap is left free (ordering and plan
// limit only) so the user can drag a handle back into one recording.
const handleBounds = computed(() => {
	const sel = props.selection;
	if (!sel) return { start: { min: 0, max: 0 }, end: { min: 0, max: 0 } };
	const range = selectionContiguous.value
		? rangeAt(props.ranges, sel.start)
		: { start: props.dvrExtent?.start ?? -Infinity, end: edgeNow() };
	return {
		start: { min: Math.max(range.start, sel.end - props.maxDuration), max: sel.end - 1 },
		end: { min: sel.start + 1, max: Math.min(range.end, sel.start + props.maxDuration) },
	};
});

const stageCursorClass = computed(() => {
	if (drag.value?.kind === 'pan') return 'cursor-grabbing';
	if (drag.value?.kind === 'handle' || hover.value?.overHandle) return 'cursor-ew-resize';
	if (drag.value?.kind === 'body' || hover.value?.overBody) return 'cursor-grab';
	if (drag.value?.kind === 'select') return 'cursor-crosshair';
	return 'cursor-pointer';
});

// ---- view helpers ---------------------------------------------------------

function maxViewDuration() {
	const extentDur = props.dvrExtent ? props.dvrExtent.end - props.dvrExtent.start : 0;
	// Never zoom out past the DVR window (plus a little room); fall back to a
	// day when the window is still unknown.
	return Math.max(extentDur * 1.1, extentDur ? 0 : 24 * 3600, 10 * 60);
}

function edgeNow() {
	const now = Date.now() / 1000;
	return Math.max(typeof props.liveEdge === 'number' ? props.liveEdge : 0, now);
}

function clampView(from, duration) {
	const dur = clamp(duration, MIN_VIEW_SEC, maxViewDuration());
	let f = from;
	const lo = props.dvrExtent ? props.dvrExtent.start - dur * 0.75 : -Infinity;
	const hi = edgeNow() - dur * 0.25;
	if (Number.isFinite(lo)) f = Math.max(f, lo);
	f = Math.min(f, hi);
	return { from: f, duration: dur };
}

function setView(from, duration, fromUser = true) {
	const next = clampView(from, duration);
	if (Math.abs(next.from - props.view.from) < 1e-6 && Math.abs(next.duration - props.view.duration) < 1e-6) return;
	emit('update:view', next);
	if (fromUser) emit('user-interaction');
}

function zoomAbout(t, factor) {
	const newDur = clamp(props.view.duration * factor, MIN_VIEW_SEC, maxViewDuration());
	const ratio = (t - props.view.from) / props.view.duration;
	setView(t - ratio * newDur, newDur);
}

function zoomBy(factor) {
	const anchor = typeof props.playhead === 'number' && props.playhead >= props.view.from && props.playhead <= viewEnd.value
		? props.playhead
		: props.view.from + props.view.duration / 2;
	zoomAbout(anchor, factor);
}

// Show the whole DVR window (recording_status extent, or the recordings we
// know about when the extent is not available yet).
function fitDvr() {
	if (props.ranges.length === 0) return;
	const start = props.dvrExtent ? Math.min(props.dvrExtent.start, props.ranges[0].start) : props.ranges[0].start;
	const end = Math.max(props.dvrExtent?.end || 0, props.ranges[props.ranges.length - 1].end, edgeNow());
	const dur = Math.max(MIN_VIEW_SEC, (end - start) * 1.02);
	setView(start - (dur - (end - start)) / 2, dur);
}

function fitSelection() {
	const sel = props.selection;
	if (!sel) return;
	const len = sel.end - sel.start;
	const dur = Math.max(MIN_VIEW_SEC, len * 1.6);
	setView(sel.start - (dur - len) / 2, dur);
}

// Pan (and only if needed, zoom out) so [start, end] is fully visible.
function ensureVisible(start, end, fromUser = false) {
	const pad = props.view.duration * 0.08;
	const len = end - start;
	if (len + pad * 2 > props.view.duration) {
		const dur = Math.max(MIN_VIEW_SEC, len * 1.4);
		setView(start - (dur - len) / 2, dur, fromUser);
		return;
	}
	if (start - pad < props.view.from) setView(start - pad, props.view.duration, fromUser);
	else if (end + pad > viewEnd.value) setView(end + pad - props.view.duration, props.view.duration, fromUser);
}

function centerOn(t) {
	setView(t - props.view.duration / 2, props.view.duration, false);
}

// ---- selection helpers ----------------------------------------------------

function emitSelection(sel) {
	emit('update:selection', sel);
	emit('user-interaction');
}

function setHandle(which, t) {
	const sel = props.selection;
	if (!sel) return;
	const b = handleBounds.value[which];
	const v = clamp(t, b.min, b.max);
	if (which === 'start') emitSelection({ start: v, end: sel.end });
	else emitSelection({ start: sel.start, end: v });
}

// Shift+drag selection. Kept raw (only trimmed to the DVR window and the plan
// limit) so a selection across a gap shows up red with an explanation
// instead of silently snapping somewhere else.
function selectBetween(anchor, t) {
	const lo = props.dvrExtent?.start ?? -Infinity;
	const hi = edgeNow();
	let start = clamp(Math.min(anchor, t), lo, hi);
	let end = clamp(Math.max(anchor, t), lo, hi);
	if (end - start < 0.5) return;
	if (end - start > props.maxDuration) {
		if (t >= anchor) end = start + props.maxDuration;
		else start = end - props.maxDuration;
	}
	emitSelection({ start, end });
}

function setInAtPlayhead() {
	const t = props.playhead;
	if (typeof t !== 'number') return;
	const sel = props.selection;
	if (sel && t < sel.end && rangeAt(props.ranges, sel.end) === rangeAt(props.ranges, t)) {
		emitSelection({ start: Math.max(t, sel.end - props.maxDuration), end: sel.end });
		return;
	}
	const range = rangeAt(props.ranges, t);
	if (!range) return;
	const end = Math.min(range.end, t + Math.min(props.maxDuration, 30));
	if (end - t < 1) return;
	emitSelection({ start: t, end });
}

function setOutAtPlayhead() {
	const t = props.playhead;
	if (typeof t !== 'number') return;
	const sel = props.selection;
	if (sel && t > sel.start && rangeAt(props.ranges, sel.start) === rangeAt(props.ranges, t)) {
		emitSelection({ start: Math.max(sel.start, t - props.maxDuration), end: t });
		return;
	}
	const range = rangeAt(props.ranges, t);
	if (!range) return;
	const start = Math.max(range.start, t - Math.min(props.maxDuration, 30));
	if (t - start < 1) return;
	emitSelection({ start, end: t });
}

// ---- pointer interactions -------------------------------------------------

function localX(e) {
	const rect = stageEl.value.getBoundingClientRect();
	return e.clientX - rect.left;
}

function localY(e) {
	const rect = stageEl.value.getBoundingClientRect();
	return e.clientY - rect.top;
}

function hitTest(x, y, pointerType = 'mouse') {
	if (props.selection && selectionVisible.value && y >= TRACK_TOP - 4) {
		const { start, end } = selectionPx.value;
		// Never let the hit zones swallow a short clip: cap at a third of it.
		const hit = Math.min(HANDLE_HIT_PX[pointerType] || HANDLE_HIT_PX.mouse, Math.max(6, (end - start) / 3));
		if (Math.abs(x - start) <= hit) return { kind: 'handle', which: 'start' };
		if (Math.abs(x - end) <= hit) return { kind: 'handle', which: 'end' };
		if (x > start && x < end) return { kind: 'body' };
	}
	return { kind: 'none' };
}

function onPointerDown(e) {
	if (e.button !== undefined && e.button !== 0) return;
	stageEl.value.focus({ preventScroll: true });
	const x = localX(e);
	const y = localY(e);
	pointers.set(e.pointerId, { x, y });
	if (pointers.size === 2) {
		const [a, b] = [...pointers.values()];
		pinchStart = { dist: Math.abs(a.x - b.x), mid: (a.x + b.x) / 2, duration: props.view.duration, from: props.view.from };
		drag.value = { kind: 'pinch' };
		return;
	}
	stageEl.value.setPointerCapture?.(e.pointerId);
	const t = xToT(x);
	if (e.shiftKey) {
		drag.value = { kind: 'select', anchor: t, moved: false };
		emit('interaction', true);
		return;
	}
	const hit = hitTest(x, y, e.pointerType);
	if (hit.kind === 'handle') {
		drag.value = { kind: 'handle', which: hit.which };
		emit('interaction', true);
		return;
	}
	if (hit.kind === 'body') {
		drag.value = { kind: 'body', startT: t, origin: { ...props.selection } };
		emit('interaction', true);
		return;
	}
	drag.value = { kind: 'pan', startX: x, startFrom: props.view.from, moved: false, startT: t };
}

function onPointerMove(e) {
	const x = localX(e);
	const y = localY(e);
	if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x, y });

	if (drag.value?.kind === 'pinch' && pinchStart && pointers.size === 2) {
		const [a, b] = [...pointers.values()];
		const dist = Math.max(10, Math.abs(a.x - b.x));
		const factor = pinchStart.dist / dist;
		const newDur = clamp(pinchStart.duration * factor, MIN_VIEW_SEC, maxViewDuration());
		const tMid = pinchStart.from + (pinchStart.mid / width.value) * pinchStart.duration;
		const mid = (a.x + b.x) / 2;
		setView(tMid - (mid / width.value) * newDur, newDur);
		return;
	}

	const d = drag.value;
	if (!d) {
		const t = xToT(x);
		const hit = hitTest(x, y, e.pointerType);
		hover.value = {
			x,
			t,
			inRange: inRange(props.ranges, t),
			overHandle: hit.kind === 'handle',
			overBody: hit.kind === 'body',
		};
		scheduleThumb(t);
		scheduleDraw();
		return;
	}

	const t = xToT(x);
	switch (d.kind) {
		case 'pan': {
			const dx = x - d.startX;
			if (!d.moved && Math.abs(dx) < 3) return;
			d.moved = true;
			const dt = (dx / width.value) * props.view.duration;
			setView(d.startFrom - dt, props.view.duration);
			break;
		}
		case 'handle':
			setHandle(d.which, t);
			break;
		case 'body': {
			const dt = t - d.startT;
			// Moving keeps the clip inside its recording; a clip that already
			// crosses a gap can be moved freely within the DVR window.
			const range = selectionIsContiguous(props.ranges, d.origin)
				? rangeAt(props.ranges, d.origin.start)
				: { start: props.dvrExtent?.start ?? -Infinity, end: edgeNow() };
			const dd = clamp(dt, range.start - d.origin.start, range.end - d.origin.end);
			emitSelection({ start: d.origin.start + dd, end: d.origin.end + dd });
			break;
		}
		case 'select':
			if (Math.abs(t - d.anchor) >= props.view.duration / width.value * 2) {
				d.moved = true;
				selectBetween(d.anchor, t);
			}
			break;
		default:
			break;
	}
}

function onPointerUp(e) {
	pointers.delete(e.pointerId);
	const d = drag.value;
	if (d?.kind === 'pinch') {
		if (pointers.size < 2) {
			pinchStart = null;
			drag.value = null;
		}
		return;
	}
	if (!d) return;
	if (d.kind === 'pan' && !d.moved) {
		emit('seek', clampSeek(d.startT));
		emit('user-interaction');
	}
	if (d.kind === 'select' && !d.moved) {
		emit('seek', clampSeek(d.anchor));
	}
	drag.value = null;
	stageEl.value?.releasePointerCapture?.(e.pointerId);
	if (d.kind === 'handle' || d.kind === 'body' || d.kind === 'select') emit('interaction', false);
}

function onPointerLeave() {
	if (!drag.value) hover.value = null;
	scheduleDraw();
}

function clampSeek(t) {
	if (inRange(props.ranges, t)) return t;
	// Snap into the nearest recording so the seek always lands on media.
	let best = null;
	let bestDist = Infinity;
	for (const r of props.ranges) {
		const c = clamp(t, r.start, r.end);
		const dist = Math.abs(c - t);
		if (dist < bestDist) {
			bestDist = dist;
			best = c;
		}
	}
	return best === null ? t : best;
}

function onWheel(e) {
	if (!width.value) return;
	e.preventDefault();
	const x = localX(e);
	if (!e.ctrlKey && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
		const dt = (e.deltaX / width.value) * props.view.duration;
		setView(props.view.from + dt, props.view.duration);
		return;
	}
	const factor = Math.exp(clamp(e.deltaY, -120, 120) * 0.0035);
	zoomAbout(xToT(x), factor);
}

// ---- keyboard -------------------------------------------------------------

function step(dt) {
	if (typeof props.playhead !== 'number') return;
	emit('seek', clampSeek(props.playhead + dt));
	emit('user-interaction');
}

function onKeyDown(e) {
	if (e.target !== stageEl.value && e.target?.getAttribute?.('role') === 'slider') return;
	let handled = true;
	switch (e.key) {
		case ' ':
		case 'k':
		case 'K':
			emit('toggle-play');
			break;
		case 'ArrowLeft':
			step(e.shiftKey ? -30 : -5);
			break;
		case 'ArrowRight':
			step(e.shiftKey ? 30 : 5);
			break;
		case 'j':
		case 'J':
			step(-10);
			break;
		case 'l':
		case 'L':
			step(10);
			break;
		case 'i':
		case 'I':
			setInAtPlayhead();
			break;
		case 'o':
		case 'O':
			setOutAtPlayhead();
			break;
		case '[':
		case ']': {
			// Held keys repeat on our own schedule (1s, then 5s, then 15s).
			if (e.repeat || !props.selection) break;
			const which = e.key === '[' ? 'start' : 'end';
			const dir = (e.key === '[' ? -1 : 1) * (e.shiftKey ? -1 : 1);
			keyHold.start(dir, { which, key: e.key });
			break;
		}
		case 'Home':
			if (props.ranges.length) {
				const t = props.ranges[0].start;
				emit('seek', t);
				centerOn(t);
				emit('user-interaction');
			}
			break;
		case 'End':
			emit('go-live');
			break;
		case '+':
		case '=':
			zoomBy(1 / 1.6);
			break;
		case '-':
		case '_':
			zoomBy(1.6);
			break;
		case 'Enter':
			// Bare Enter is left alone so it cannot trigger a download by accident.
			if (!(e.metaKey || e.ctrlKey)) {
				handled = false;
				break;
			}
			emit('download');
			break;
		case 'Escape':
			if (showShortcuts.value) showShortcuts.value = false;
			else emitSelection(null);
			break;
		case '?':
			showShortcuts.value = !showShortcuts.value;
			break;
		default:
			handled = false;
	}
	if (handled) e.preventDefault();
}

const keyHold = createHoldRepeater({
	onStart: () => emit('interaction', true),
	onStep: (dt, { which }) => {
		if (props.selection) setHandle(which, props.selection[which] + dt);
	},
	onEnd: () => emit('interaction', false),
});

function onKeyUp(e) {
	if (e.key === '[' || e.key === ']' || e.key === 'Shift') stopKeyHold();
}

function stopKeyHold() {
	keyHold.stop();
}

function onHandleKey(e, which) {
	const sel = props.selection;
	if (!sel) return;
	const big = e.shiftKey ? 5 : 1;
	let handled = true;
	switch (e.key) {
		case 'ArrowLeft':
		case 'ArrowDown':
			setHandle(which, sel[which] - big);
			break;
		case 'ArrowRight':
		case 'ArrowUp':
			setHandle(which, sel[which] + big);
			break;
		case 'Home':
			setHandle(which, handleBounds.value[which].min);
			break;
		case 'End':
			setHandle(which, handleBounds.value[which].max);
			break;
		case 'Escape':
			emitSelection(null);
			break;
		default:
			handled = false;
	}
	if (handled) e.preventDefault();
}

// ---- thumbnails -----------------------------------------------------------

function scheduleThumb(t) {
	if (!props.baseUrl) return;
	const sec = Math.floor(t);
	if (sec === lastThumbSec) return;
	clearTimeout(thumbTimer);
	thumbTimer = setTimeout(() => {
		lastThumbSec = sec;
		if (!inRange(props.ranges, sec)) return;
		thumbBroken.value = false;
		thumbSrc.value = thumbnailUrl(props.baseUrl, sec);
	}, 120);
}

// ---- drawing --------------------------------------------------------------

const COLORS = {
	bg: 'rgb(20, 30, 61)',
	track: 'rgb(27, 38, 72)',
	future: 'rgb(16, 25, 54)',
	tick: 'rgba(174, 186, 214, 0.35)',
	tickMinor: 'rgba(174, 186, 214, 0.15)',
	label: 'rgb(174, 186, 214)',
	range: 'rgb(38, 132, 210)',
	rangeDim: 'rgba(38, 132, 210, 0.38)',
	gap: 'rgb(12, 18, 40)',
	gapHatch: 'rgba(174, 186, 214, 0.2)',
	gapLabel: 'rgba(174, 186, 214, 0.75)',
	selection: 'rgba(0, 98, 255, 0.55)',
	selectionBorder: 'rgb(32, 118, 255)',
	selectionBad: 'rgba(226, 88, 88, 0.5)',
	selectionBadBorder: 'rgb(244, 100, 100)',
	exportBand: 'rgba(0, 98, 255, 0.18)',
	exportOutline: 'rgba(120, 170, 255, 0.85)',
	handle: '#ffffff',
	playhead: '#ffffff',
	live: 'rgb(226, 88, 88)',
	hover: 'rgba(255, 255, 255, 0.35)',
};

let hatchPattern = null;

function makeHatch(c) {
	const size = 8;
	const off = document.createElement('canvas');
	off.width = size * dpr;
	off.height = size * dpr;
	const octx = off.getContext('2d');
	octx.scale(dpr, dpr);
	octx.strokeStyle = COLORS.gapHatch;
	octx.lineWidth = 1;
	octx.beginPath();
	octx.moveTo(-2, size + 2);
	octx.lineTo(size + 2, -2);
	octx.moveTo(-2, size / 2 + 2);
	octx.lineTo(size / 2 + 2, -2);
	octx.moveTo(size / 2 - 2, size + 2);
	octx.lineTo(size + 2, size / 2 - 2);
	octx.stroke();
	const pattern = c.createPattern(off, 'repeat');
	const m = typeof DOMMatrix !== 'undefined' ? new DOMMatrix() : null;
	if (pattern && m && pattern.setTransform) pattern.setTransform(m.scale(1 / dpr, 1 / dpr));
	return pattern;
}

function scheduleDraw() {
	if (rafId) return;
	rafId = requestAnimationFrame(() => {
		rafId = 0;
		draw();
	});
}

function roundRect(c, x, y, w, h, r) {
	const rr = Math.min(r, w / 2, h / 2);
	c.beginPath();
	c.moveTo(x + rr, y);
	c.lineTo(x + w - rr, y);
	c.quadraticCurveTo(x + w, y, x + w, y + rr);
	c.lineTo(x + w, y + h - rr);
	c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
	c.lineTo(x + rr, y + h);
	c.quadraticCurveTo(x, y + h, x, y + h - rr);
	c.lineTo(x, y + rr);
	c.quadraticCurveTo(x, y, x + rr, y);
	c.closePath();
}

function draw() {
	const c = ctx;
	const W = width.value;
	if (!c || !W) return;
	const H = STAGE_HEIGHT;
	c.save();
	c.setTransform(dpr, 0, 0, dpr, 0, 0);
	c.clearRect(0, 0, W, H);
	c.fillStyle = COLORS.bg;
	c.fillRect(0, 0, W, H);

	const from = props.view.from;
	const dur = props.view.duration;
	const to = from + dur;
	const sel = props.selection;
	const hasSel = !!sel;

	// Track background
	c.fillStyle = COLORS.track;
	c.fillRect(0, TRACK_TOP, W, TRACK_HEIGHT);

	// Gaps between recordings: dark, cross-hatched, labelled when wide enough
	const gapLabels = [];
	if (props.ranges.length) {
		const last = props.ranges[props.ranges.length - 1].end;
		for (const g of gapsBetween(props.ranges)) {
			if (g.end < from || g.start > to) continue;
			const x0 = clamp(tToX(g.start), 0, W);
			const x1 = clamp(tToX(g.end), 0, W);
			if (x1 - x0 < 0.5) continue;
			c.fillStyle = COLORS.gap;
			c.fillRect(x0, TRACK_TOP, x1 - x0, TRACK_HEIGHT);
			if (hatchPattern) {
				c.fillStyle = hatchPattern;
				c.fillRect(x0, TRACK_TOP, x1 - x0, TRACK_HEIGHT);
			}
			if (x1 - x0 >= 96) gapLabels.push((x0 + x1) / 2);
		}
		// Future (past live edge) darker
		const edge = Math.max(last, typeof props.liveEdge === 'number' ? props.liveEdge : last);
		if (edge < to) {
			const x0 = clamp(tToX(edge), 0, W);
			c.fillStyle = COLORS.future;
			c.fillRect(x0, TRACK_TOP, W - x0, TRACK_HEIGHT);
		}
		if (props.dvrExtent && props.dvrExtent.start > from) {
			const x1 = clamp(tToX(props.dvrExtent.start), 0, W);
			c.fillStyle = COLORS.future;
			c.fillRect(0, TRACK_TOP, x1, TRACK_HEIGHT);
		}
	}

	// Recorded ranges
	for (const r of props.ranges) {
		if (r.end < from || r.start > to) continue;
		const x0 = clamp(tToX(r.start), -2, W + 2);
		const x1 = clamp(tToX(r.end), -2, W + 2);
		c.fillStyle = hasSel ? COLORS.rangeDim : COLORS.range;
		roundRect(c, x0, TRACK_TOP + 6, Math.max(2, x1 - x0), TRACK_HEIGHT - 12, 4);
		c.fill();
	}

	// Gap labels go over the bars (the translucent selection keeps them readable)
	if (gapLabels.length) {
		c.font = '600 10px Inter, ui-sans-serif, system-ui, sans-serif';
		c.textAlign = 'center';
		c.textBaseline = 'middle';
		c.fillStyle = COLORS.gapLabel;
		for (const gx of gapLabels) c.fillText('NO RECORDING', gx, TRACK_TOP + TRACK_HEIGHT / 2);
	}

	// Selection
	if (hasSel && sel.end >= from && sel.start <= to) {
		const bad = selectionBad.value;
		const x0 = clamp(tToX(sel.start), -2, W + 2);
		const x1 = clamp(tToX(sel.end), -2, W + 2);
		// Exported extension: the segment-aligned window the file will cover,
		// drawn as a thin hatched band with a dashed outline around the clip.
		const exp = props.exportWindow;
		if (!bad && exp && (exp.start < sel.start - 0.01 || exp.end > sel.end + 0.01)) {
			const ex0 = clamp(tToX(exp.start), -2, W + 2);
			const ex1 = clamp(tToX(exp.end), -2, W + 2);
			c.save();
			c.fillStyle = COLORS.exportBand;
			if (ex0 < x0) c.fillRect(ex0, TRACK_TOP, x0 - ex0, TRACK_HEIGHT);
			if (ex1 > x1) c.fillRect(x1, TRACK_TOP, ex1 - x1, TRACK_HEIGHT);
			if (hatchPattern) {
				c.fillStyle = hatchPattern;
				if (ex0 < x0) c.fillRect(ex0, TRACK_TOP, x0 - ex0, TRACK_HEIGHT);
				if (ex1 > x1) c.fillRect(x1, TRACK_TOP, ex1 - x1, TRACK_HEIGHT);
			}
			c.setLineDash([3, 3]);
			c.strokeStyle = COLORS.exportOutline;
			c.lineWidth = 1;
			c.strokeRect(Math.round(ex0) + 0.5, TRACK_TOP + 0.5, Math.max(1, Math.round(ex1 - ex0)) - 1, TRACK_HEIGHT - 1);
			c.restore();
		}
		c.fillStyle = bad ? COLORS.selectionBad : COLORS.selection;
		c.fillRect(x0, TRACK_TOP, Math.max(1, x1 - x0), TRACK_HEIGHT);
		c.strokeStyle = bad ? COLORS.selectionBadBorder : COLORS.selectionBorder;
		c.lineWidth = 1.5;
		c.strokeRect(x0 + 0.75, TRACK_TOP + 0.75, Math.max(1, x1 - x0) - 1.5, TRACK_HEIGHT - 1.5);
		// Handles
		for (const hx of [x0, x1]) {
			c.fillStyle = COLORS.handle;
			roundRect(c, hx - 4, TRACK_TOP + 10, 8, TRACK_HEIGHT - 20, 3);
			c.fill();
			c.strokeStyle = 'rgba(0, 98, 255, 0.9)';
			c.lineWidth = 1;
			c.stroke();
			c.strokeStyle = 'rgba(0, 78, 204, 0.9)';
			c.beginPath();
			c.moveTo(hx - 1.5, TRACK_TOP + TRACK_HEIGHT / 2 - 6);
			c.lineTo(hx - 1.5, TRACK_TOP + TRACK_HEIGHT / 2 + 6);
			c.moveTo(hx + 1.5, TRACK_TOP + TRACK_HEIGHT / 2 - 6);
			c.lineTo(hx + 1.5, TRACK_TOP + TRACK_HEIGHT / 2 + 6);
			c.stroke();
		}
	}

	// Ruler
	const { major, minor } = pickTickStep(dur, W);
	c.fillStyle = 'rgba(20, 30, 61, 0.9)';
	c.fillRect(0, 0, W, RULER_HEIGHT);
	c.font = '11px Inter, ui-sans-serif, system-ui, sans-serif';
	c.textBaseline = 'middle';
	c.textAlign = 'center';
	const tzOffsetSec = new Date(from * 1000).getTimezoneOffset() * -60;
	const alignedStart = Math.floor((from + tzOffsetSec) / minor) * minor - tzOffsetSec;
	let lastDay = null;
	for (let t = alignedStart; t <= to + minor; t += minor) {
		const x = tToX(t);
		if (x < -40 || x > W + 40) continue;
		const isMajor = Math.abs(((t + tzOffsetSec) % major + major) % major) < 1e-6;
		c.strokeStyle = isMajor ? COLORS.tick : COLORS.tickMinor;
		c.lineWidth = 1;
		c.beginPath();
		c.moveTo(Math.round(x) + 0.5, isMajor ? RULER_HEIGHT - 6 : RULER_HEIGHT - 3);
		c.lineTo(Math.round(x) + 0.5, TRACK_TOP + TRACK_HEIGHT);
		c.stroke();
		if (isMajor) {
			const day = formatLocalDate(t);
			let label = formatLocalTime(t, major < 60);
			if (dur > 12 * 3600 && day !== lastDay) label = `${day} ${label}`;
			lastDay = day;
			c.fillStyle = COLORS.label;
			c.fillText(label, x, 9);
		}
	}

	// Hover line
	if (hover.value && !drag.value) {
		c.strokeStyle = COLORS.hover;
		c.lineWidth = 1;
		c.beginPath();
		c.moveTo(Math.round(hover.value.x) + 0.5, RULER_HEIGHT);
		c.lineTo(Math.round(hover.value.x) + 0.5, H);
		c.stroke();
	}

	// Live edge
	if (typeof props.liveEdge === 'number') {
		const x = tToX(props.liveEdge);
		if (x >= -1 && x <= W + 1) {
			c.strokeStyle = COLORS.live;
			c.lineWidth = 2;
			c.beginPath();
			c.moveTo(Math.round(x), RULER_HEIGHT - 2);
			c.lineTo(Math.round(x), H);
			c.stroke();
		}
	}

	// Playhead
	if (typeof props.playhead === 'number') {
		const x = tToX(props.playhead);
		if (x >= -1 && x <= W + 1) {
			const px = Math.round(x);
			c.strokeStyle = COLORS.playhead;
			c.lineWidth = 2;
			c.beginPath();
			c.moveTo(px, RULER_HEIGHT - 4);
			c.lineTo(px, H);
			c.stroke();
			c.fillStyle = COLORS.playhead;
			c.beginPath();
			c.moveTo(px - 6, RULER_HEIGHT - 8);
			c.lineTo(px + 6, RULER_HEIGHT - 8);
			c.lineTo(px, RULER_HEIGHT);
			c.closePath();
			c.fill();
		}
	}

	c.restore();
}

function resize() {
	const stage = stageEl.value;
	const canvas = canvasEl.value;
	if (!stage || !canvas) return;
	const rect = stage.getBoundingClientRect();
	width.value = Math.max(0, Math.floor(rect.width));
	dpr = Math.min(3, window.devicePixelRatio || 1);
	canvas.width = Math.floor(width.value * dpr);
	canvas.height = Math.floor(STAGE_HEIGHT * dpr);
	canvas.style.width = `${width.value}px`;
	canvas.style.height = `${STAGE_HEIGHT}px`;
	ctx = canvas.getContext('2d');
	hatchPattern = ctx ? makeHatch(ctx) : null;
	scheduleDraw();
}

// ---- follow the playhead --------------------------------------------------

watch(() => props.playhead, (t, prev) => {
	if (!props.follow || typeof t !== 'number' || drag.value) return;
	const end = viewEnd.value;
	// Only follow when the playhead actually crosses the right edge on its own;
	// a user who panned away keeps their view.
	if (t > end && typeof prev === 'number' && prev <= end && t - end < props.view.duration) {
		// Advance so the playhead sits at ~75% of the view.
		setView(t - props.view.duration * 0.75, props.view.duration, false);
	}
});

watch(
	() => [props.ranges, props.view, props.playhead, props.liveEdge, props.selection, props.maxDuration, props.dvrExtent, props.exportWindow],
	() => scheduleDraw(),
	{ deep: true },
);

onMounted(() => {
	resize();
	resizeObserver = new ResizeObserver(() => resize());
	resizeObserver.observe(stageEl.value);
	stageEl.value.addEventListener('wheel', onWheel, { passive: false });
});

onBeforeUnmount(() => {
	keyHold.stop(false);
	resizeObserver?.disconnect();
	stageEl.value?.removeEventListener('wheel', onWheel);
	cancelAnimationFrame(rafId);
	clearTimeout(thumbTimer);
});

defineExpose({
	fitDvr,
	fitSelection,
	ensureVisible,
	zoomBy,
	centerOn,
	handleKey: onKeyDown,
	handleKeyUp: onKeyUp,
	focus: () => stageEl.value?.focus({ preventScroll: true }),
});
</script>

<style scoped>
.clip-timeline__stage {
	touch-action: pan-y;
}

.clip-timeline__handle {
	position: absolute;
	width: 28px;
	margin-left: -8px; /* centre the 28px box on the 12px visual */
	cursor: ew-resize;
	border-radius: 4px;
	outline: none;
}

@media (pointer: coarse) {
	.clip-timeline__handle {
		width: 44px;
		margin-left: -16px;
	}
}

/* Ghost toolbar buttons: 28px visual, 44px hit area, no filled disabled state */
.clip-timeline__tool {
	position: relative;
	overflow: visible !important; /* FormButton truncates; the hit area must escape the box */
}

.clip-timeline__tool::after {
	content: '';
	position: absolute;
	inset: -8px;
}

.clip-timeline__tool:disabled {
	background: transparent !important;
}

.clip-timeline__skeleton > * {
	animation: clip-timeline-shimmer 1.4s ease-in-out infinite;
}

@keyframes clip-timeline-shimmer {
	0%,
	100% {
		opacity: 0.45;
	}
	50% {
		opacity: 1;
	}
}

.clip-timeline__kbd {
	display: inline-block;
	min-width: 18px;
	padding: 0 5px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.08);
	box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.12);
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 10px;
	line-height: 16px;
	text-align: center;
	color: rgb(209, 219, 240);
}

.clip-timeline__handle:focus-visible {
	box-shadow: 0 0 0 2px rgba(32, 118, 255, 0.9);
}
</style>
